<template>
    <Head title="Create Checklist" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
            <div>
                <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Create Ad-Hoc Checklist</h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Create a one-time checklist that will be assigned immediately</p>
            </div>
            <Link
                :href="route('checklists.index')"
                class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
                Back to Checklists
            </Link>
        </div>

        <!-- Form -->
        <form @submit.prevent="submit" class="space-y-6">
            <!-- Basic Information -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Basic Information</h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Template -->
                    <div class="md:col-span-2">
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Checklist Template <span class="text-red-500">*</span>
                        </label>
                        <select
                            v-model="form.template_id"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            required
                        >
                            <option value="">Select Template</option>
                            <option v-for="template in templates" :key="template.id" :value="template.id">
                                {{ template.title }} ({{ template.template_code }})
                            </option>
                        </select>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Select the checklist template to use
                        </p>
                        <p v-if="errors.template_id" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.template_id }}</p>
                    </div>

                    <!-- SLA Hours and Minutes -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Completion SLA <span class="text-red-500">*</span>
                        </label>
                        <div class="grid grid-cols-2 gap-2">
                            <div>
                                <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Hours</label>
                                <input
                                    v-model.number="form.sla_hours"
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="0"
                                    @keypress="preventDecimal"
                                    @input="stripDecimal($event, 'sla_hours')"
                                    class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Minutes</label>
                                <input
                                    v-model.number="form.sla_minutes"
                                    type="number"
                                    min="0"
                                    max="59"
                                    step="1"
                                    placeholder="0"
                                    @keypress="preventDecimal"
                                    @input="stripDecimal($event, 'sla_minutes')"
                                    class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Time allowed to complete checklist from now
                        </p>
                        <p v-if="slaError" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ slaError }}</p>
                    </div>

                    <!-- Expires After Hours and Minutes -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Expires After (Optional)
                        </label>
                        <div class="grid grid-cols-2 gap-2">
                            <div>
                                <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Hours</label>
                                <input
                                    v-model.number="form.expires_hours"
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="0"
                                    @keypress="preventDecimal"
                                    @input="stripDecimal($event, 'expires_hours')"
                                    class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Minutes</label>
                                <input
                                    v-model.number="form.expires_minutes"
                                    type="number"
                                    min="0"
                                    max="59"
                                    step="1"
                                    placeholder="0"
                                    @keypress="preventDecimal"
                                    @input="stripDecimal($event, 'expires_minutes')"
                                    class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            If set, checklist will automatically expire after this time. Leave blank for no expiry.
                        </p>
                        <p v-if="expiresError" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ expiresError }}</p>
                    </div>
                </div>
            </div>

            <!-- Location and Asset -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Location and Asset (Optional)</h2>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Linking a location or asset enables deeper reporting analytics to track performance, completion rates, and compliance metrics.
                </p>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Location -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Select Location
                        </label>
                        <select
                            v-model="form.location_id"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                            <option value="">No Location</option>
                            <option v-for="location in locations" :key="location.id" :value="location.id">
                                {{ location.name }}
                            </option>
                        </select>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Link this checklist to a specific location
                        </p>
                    </div>

                    <!-- Asset -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Select Asset
                        </label>
                        <select
                            v-model="form.asset_id"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                            <option value="">No Asset</option>
                            <option v-for="asset in assets" :key="asset.id" :value="asset.id">
                                {{ asset.name }} ({{ asset.asset_tag }})
                            </option>
                        </select>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Link this checklist to a specific asset for asset-based maintenance tracking
                        </p>
                    </div>
                </div>
            </div>

            <!-- Checklist Manager Access Control -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Checklist Manager Access Control (Optional)</h2>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    By default, all users with the Checklist Manager role can access and manage checklists. If you want to restrict access to specific Checklist Managers, select them below.
                </p>

                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Select Checklist Managers (Optional)
                    </label>
                    <div class="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded p-2">
                        <label v-for="user in checklistManagerUsers" :key="user.id" class="flex items-center text-xs">
                            <input
                                v-model="form.checklist_manager_ids"
                                type="checkbox"
                                :value="user.id"
                                class="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                            />
                            <span class="ml-2 text-gray-700 dark:text-gray-300">{{ user.name }}</span>
                        </label>
                        <div v-if="!checklistManagerUsers || checklistManagerUsers.length === 0" class="col-span-3 text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                            No users with Checklist Manager role found
                        </div>
                    </div>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Selected: {{ form.checklist_manager_ids.length }} manager(s)</p>
                </div>
            </div>

            <!-- Workflow Steps -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Workflow Configuration</h2>

                <!-- Conduct Step (Mandatory) -->
                <div class="border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20 p-4 mb-4 rounded">
                    <div class="flex items-start">
                        <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                            </svg>
                        </div>
                        <div class="ml-3 flex-1">
                            <h3 class="text-sm font-medium text-blue-800 dark:text-blue-200">
                                Step 1: Conduct/Fill Checklist <span class="text-red-500">*</span>
                            </h3>
                            <p class="mt-1 text-xs text-blue-700 dark:text-blue-300">
                                This step is mandatory. Select users who will fill out the checklist.
                            </p>

                            <div class="mt-3 space-y-3">
                                <!-- Assignment Mode -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Assignment Mode <span class="text-red-500">*</span>
                                    </label>
                                    <select
                                        v-model="form.conduct_assignment_type"
                                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                        required
                                    >
                                        <option value="first_to_start">Any assigned can start (first to start locks the checklist)</option>
                                        <option value="all_users">Specific users (all need to complete separately)</option>
                                    </select>
                                </div>

                                <!-- Assigned Users -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Assigned Users <span class="text-red-500">*</span>
                                    </label>
                                    <div class="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded p-2">
                                        <label v-for="user in users" :key="user.id" class="flex items-center text-xs">
                                            <input
                                                v-model="form.conduct_user_ids"
                                                type="checkbox"
                                                :value="user.id"
                                                class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span class="ml-2 text-gray-700 dark:text-gray-300">{{ user.first_name }} {{ user.last_name }}</span>
                                        </label>
                                    </div>
                                    <p v-if="conductUsersError" class="mt-1 text-xs text-red-600 dark:text-red-400">
                                        {{ conductUsersError }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Optional Approval Steps -->
                <div class="mt-6">
                    <div class="flex justify-between items-center mb-4">
                        <div>
                            <h3 class="text-sm font-medium text-gray-900 dark:text-white">Approval Levels (Optional)</h3>
                            <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">Add up to 3 approval levels. Approvals will be executed in sequential order.</p>
                        </div>
                        <button
                            @click="addApprover"
                            type="button"
                            :disabled="activeApprovalCount >= 3"
                            class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Add Approver
                        </button>
                    </div>

                    <div class="space-y-3">
                        <!-- Approval Level 1 -->
                        <div v-if="form.approval_1_required" class="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                            <div class="flex items-start justify-between mb-3">
                                <h4 class="text-sm font-medium text-gray-900 dark:text-white">
                                    Approval Level 1
                                </h4>
                                <button
                                    @click="removeApprover(1)"
                                    type="button"
                                    class="text-red-500 hover:text-red-700"
                                >
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>

                            <div class="grid grid-cols-1 gap-3">
                                <!-- Step Name -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Step Name (Optional)
                                    </label>
                                    <input
                                        v-model="form.approval_1_step_name"
                                        type="text"
                                        placeholder="e.g., Manager Approval"
                                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                    />
                                </div>

                                <!-- Assigned User -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Assigned To <span class="text-red-500">*</span>
                                    </label>
                                    <select
                                        v-model="form.approval_1_user_id"
                                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                        required
                                    >
                                        <option value="">Select User</option>
                                        <option v-for="user in users" :key="user.id" :value="user.id">
                                            {{ user.first_name }} {{ user.last_name }}
                                        </option>
                                    </select>
                                </div>

                                <!-- Auto Approve -->
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Auto-approve after (Optional)
                                    </label>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div>
                                            <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Hours</label>
                                            <input
                                                v-model.number="form.approval_1_auto_approve_hours"
                                                type="number"
                                                min="0"
                                                step="1"
                                                placeholder="0"
                                                @keypress="preventDecimal"
                                                @input="stripDecimal($event, 'approval_1_auto_approve_hours')"
                                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Minutes</label>
                                            <input
                                                v-model.number="form.approval_1_auto_approve_minutes"
                                                type="number"
                                                min="0"
                                                max="59"
                                                step="1"
                                                placeholder="0"
                                                @keypress="preventDecimal"
                                                @input="stripDecimal($event, 'approval_1_auto_approve_minutes')"
                                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                    </div>
                                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        If set, checklist will be auto-approved after this time if approver hasn't taken action. Leave blank to disable.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Approval Level 2 -->
                        <div v-if="form.approval_2_required" class="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                            <div class="flex items-start justify-between mb-3">
                                <h4 class="text-sm font-medium text-gray-900 dark:text-white">
                                    Approval Level 2
                                </h4>
                                <button
                                    @click="removeApprover(2)"
                                    type="button"
                                    class="text-red-500 hover:text-red-700"
                                >
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>

                            <div class="grid grid-cols-1 gap-3">
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Step Name (Optional)
                                    </label>
                                    <input
                                        v-model="form.approval_2_step_name"
                                        type="text"
                                        placeholder="e.g., Director Approval"
                                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                    />
                                </div>

                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Assigned To <span class="text-red-500">*</span>
                                    </label>
                                    <select
                                        v-model="form.approval_2_user_id"
                                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                        required
                                    >
                                        <option value="">Select User</option>
                                        <option v-for="user in users" :key="user.id" :value="user.id">
                                            {{ user.first_name }} {{ user.last_name }}
                                        </option>
                                    </select>
                                </div>

                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Auto-approve after (Optional)
                                    </label>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div>
                                            <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Hours</label>
                                            <input
                                                v-model.number="form.approval_2_auto_approve_hours"
                                                type="number"
                                                min="0"
                                                step="1"
                                                placeholder="0"
                                                @keypress="preventDecimal"
                                                @input="stripDecimal($event, 'approval_2_auto_approve_hours')"
                                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Minutes</label>
                                            <input
                                                v-model.number="form.approval_2_auto_approve_minutes"
                                                type="number"
                                                min="0"
                                                max="59"
                                                step="1"
                                                placeholder="0"
                                                @keypress="preventDecimal"
                                                @input="stripDecimal($event, 'approval_2_auto_approve_minutes')"
                                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                    </div>
                                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        If set, checklist will be auto-approved after this time if approver hasn't taken action. Leave blank to disable.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Approval Level 3 -->
                        <div v-if="form.approval_3_required" class="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                            <div class="flex items-start justify-between mb-3">
                                <h4 class="text-sm font-medium text-gray-900 dark:text-white">
                                    Approval Level 3
                                </h4>
                                <button
                                    @click="removeApprover(3)"
                                    type="button"
                                    class="text-red-500 hover:text-red-700"
                                >
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>

                            <div class="grid grid-cols-1 gap-3">
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Step Name (Optional)
                                    </label>
                                    <input
                                        v-model="form.approval_3_step_name"
                                        type="text"
                                        placeholder="e.g., Senior Director Approval"
                                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                    />
                                </div>

                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Assigned To <span class="text-red-500">*</span>
                                    </label>
                                    <select
                                        v-model="form.approval_3_user_id"
                                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                        required
                                    >
                                        <option value="">Select User</option>
                                        <option v-for="user in users" :key="user.id" :value="user.id">
                                            {{ user.first_name }} {{ user.last_name }}
                                        </option>
                                    </select>
                                </div>

                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Auto-approve after (Optional)
                                    </label>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div>
                                            <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Hours</label>
                                            <input
                                                v-model.number="form.approval_3_auto_approve_hours"
                                                type="number"
                                                min="0"
                                                step="1"
                                                placeholder="0"
                                                @keypress="preventDecimal"
                                                @input="stripDecimal($event, 'approval_3_auto_approve_hours')"
                                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">Minutes</label>
                                            <input
                                                v-model.number="form.approval_3_auto_approve_minutes"
                                                type="number"
                                                min="0"
                                                max="59"
                                                step="1"
                                                placeholder="0"
                                                @keypress="preventDecimal"
                                                @input="stripDecimal($event, 'approval_3_auto_approve_minutes')"
                                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                    </div>
                                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        If set, checklist will be auto-approved after this time if approver hasn't taken action. Leave blank to disable.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div v-if="!form.approval_1_required && !form.approval_2_required && !form.approval_3_required" class="text-center py-6 text-gray-500 dark:text-gray-400 text-sm border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                            No approval levels added. Click "Add Approver" to create optional approval workflow.
                        </div>
                    </div>
                </div>

                <!-- Notification Step (Optional) -->
                <div class="mt-6 border-l-4 border-purple-500 bg-purple-50 dark:bg-purple-900/20 p-4 rounded">
                    <div class="flex items-start">
                        <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                            </svg>
                        </div>
                        <div class="ml-3 flex-1">
                            <h3 class="text-sm font-medium text-purple-800 dark:text-purple-200">
                                Final Step: Notify Users (Optional)
                            </h3>
                            <p class="mt-1 text-xs text-purple-700 dark:text-purple-300">
                                Select users to notify when the checklist is completed (after all approvals are done, if any).
                            </p>

                            <div class="mt-3">
                                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Notify Users
                                </label>
                                <div class="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700">
                                    <label v-for="user in users" :key="user.id" class="flex items-center text-xs">
                                        <input
                                            v-model="form.notify_user_ids"
                                            type="checkbox"
                                            :value="user.id"
                                            class="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                                        />
                                        <span class="ml-2 text-gray-700 dark:text-gray-300">{{ user.first_name }} {{ user.last_name }}</span>
                                    </label>
                                </div>
                                <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    Leave unselected if you don't want to send notifications when checklist is completed.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <div class="flex justify-end space-x-3">
                <Link
                    :href="route('checklists.index')"
                    class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                    Cancel
                </Link>
                <button
                    type="submit"
                    :disabled="form.processing"
                    class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                    {{ form.processing ? 'Creating...' : 'Create Checklist' }}
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import route from '@/Utils/route';
import { useSecureForm } from '@/Composables/useSecureForm';
import { ref, computed } from 'vue';

const props = defineProps({
    templates: Array,
    locations: Array,
    assets: Array,
    users: Array,
    checklistManagerUsers: Array,
    errors: Object,
});

const form = useSecureForm({
    template_id: '',
    location_id: '',
    asset_id: '',

    // SLA and Expiry
    sla_hours: 0,
    sla_minutes: 0,
    expires_hours: null,
    expires_minutes: null,

    // Conduct Step (Mandatory)
    conduct_assignment_type: 'first_to_start',
    conduct_user_ids: [],

    // Approval Level 1
    approval_1_required: false,
    approval_1_step_name: '',
    approval_1_user_id: '',
    approval_1_auto_approve_hours: null,
    approval_1_auto_approve_minutes: null,

    // Approval Level 2
    approval_2_required: false,
    approval_2_step_name: '',
    approval_2_user_id: '',
    approval_2_auto_approve_hours: null,
    approval_2_auto_approve_minutes: null,

    // Approval Level 3
    approval_3_required: false,
    approval_3_step_name: '',
    approval_3_user_id: '',
    approval_3_auto_approve_hours: null,
    approval_3_auto_approve_minutes: null,

    // Notify Users
    notify_user_ids: [],

    // Checklist Manager Access Control
    checklist_manager_ids: [],
});

// Computed error messages
const conductUsersError = computed(() => {
    if (form.conduct_user_ids.length === 0) {
        return 'Please select at least one user to conduct the checklist';
    }
    return null;
});

const slaError = computed(() => {
    const totalMinutes = (form.sla_hours || 0) * 60 + (form.sla_minutes || 0);
    if (totalMinutes === 0) {
        return 'SLA must be at least 1 minute';
    }
    return null;
});

const expiresError = computed(() => {
    const totalMinutes = (form.expires_hours || 0) * 60 + (form.expires_minutes || 0);
    if (totalMinutes > 0) {
        const slaMinutes = (form.sla_hours || 0) * 60 + (form.sla_minutes || 0);
        if (totalMinutes <= slaMinutes) {
            return 'Expiry time must be greater than SLA time';
        }
    }
    return null;
});

// Count active approvals
const activeApprovalCount = computed(() => {
    let count = 0;
    if (form.approval_1_required) count++;
    if (form.approval_2_required) count++;
    if (form.approval_3_required) count++;
    return count;
});

// Prevent decimal input
const preventDecimal = (event) => {
    if (event.key === '.' || event.key === ',') {
        event.preventDefault();
    }
};

// Strip decimal from input
const stripDecimal = (event, fieldName) => {
    const value = event.target.value;
    if (value.includes('.') || value.includes(',')) {
        form[fieldName] = parseInt(value) || 0;
    }
};

// Add approver
const addApprover = () => {
    if (!form.approval_1_required) {
        form.approval_1_required = true;
    } else if (!form.approval_2_required) {
        form.approval_2_required = true;
    } else if (!form.approval_3_required) {
        form.approval_3_required = true;
    }
};

// Remove approver
const removeApprover = (level) => {
    if (level === 1) {
        form.approval_1_required = false;
        form.approval_1_step_name = '';
        form.approval_1_user_id = '';
        form.approval_1_auto_approve_hours = null;
        form.approval_1_auto_approve_minutes = null;
    } else if (level === 2) {
        form.approval_2_required = false;
        form.approval_2_step_name = '';
        form.approval_2_user_id = '';
        form.approval_2_auto_approve_hours = null;
        form.approval_2_auto_approve_minutes = null;
    } else if (level === 3) {
        form.approval_3_required = false;
        form.approval_3_step_name = '';
        form.approval_3_user_id = '';
        form.approval_3_auto_approve_hours = null;
        form.approval_3_auto_approve_minutes = null;
    }
};

// Submit form
const submit = () => {
    // Validate conduct users
    if (form.conduct_user_ids.length === 0) {
        alert('Please select at least one user to conduct the checklist');
        return;
    }

    // Validate SLA
    const totalSlaMinutes = (form.sla_hours || 0) * 60 + (form.sla_minutes || 0);
    if (totalSlaMinutes === 0) {
        alert('Please set a completion SLA time');
        return;
    }

    // Validate expiry if set
    const totalExpiresMinutes = (form.expires_hours || 0) * 60 + (form.expires_minutes || 0);
    if (totalExpiresMinutes > 0 && totalExpiresMinutes <= totalSlaMinutes) {
        alert('Expiry time must be greater than SLA time');
        return;
    }

    // Validate approval assignments
    if (form.approval_1_required && !form.approval_1_user_id) {
        alert('Please assign a user to Approval Level 1');
        return;
    }
    if (form.approval_2_required && !form.approval_2_user_id) {
        alert('Please assign a user to Approval Level 2');
        return;
    }
    if (form.approval_3_required && !form.approval_3_user_id) {
        alert('Please assign a user to Approval Level 3');
        return;
    }

    form.post(route('checklists.store'));
};
</script>
